import { nextTick, Ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { AmsItem } from '../../services/amsSearch/amsSearch.interface'
import AmsListItem from '../../components/AmsListItem'

export const useItemsIntersectionObserver = ({
    listRef,
    itemsComponents,
    onItemInViewport,
}: {
    listRef: Ref<HTMLElement | undefined>
    itemsComponents: Ref<InstanceType<typeof AmsListItem>[] | undefined>
    onItemInViewport: (item: AmsItem) => void
}) => {
    let observer: IntersectionObserver

    const itemsMap = computed(
        () =>
            new Map<Element, AmsItem>(
                itemsComponents.value?.map((component) => [component.$el, component.$props.item]),
            ),
    )

    const interSectionOptions: IntersectionObserverInit = {
        root: listRef.value,
        threshold: 1.0,
    }

    watch(
        itemsComponents,
        async (newValue) => {
            if (newValue?.length) {
                await nextTick()
                observeItems()
            }
        },
        { deep: true },
    )

    function observeItems() {
        for (const element of itemsMap.value?.keys()) {
            observer.observe(element)
        }
    }

    function handleIntersect(entries: IntersectionObserverEntry[], observer: IntersectionObserver) {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                const itemInViewport = itemsMap.value.get(entry.target)
                if (itemInViewport) {
                    onItemInViewport(itemInViewport)
                }
                observer.unobserve(entry.target)
            }
        })
    }

    onMounted(() => {
        observer = new IntersectionObserver(handleIntersect, interSectionOptions)
        observeItems()
    })

    onUnmounted(() => observer.disconnect())
}
